import { motion } from 'framer-motion';
import { SKILL_AREAS, computeOverallScore } from '../data/skillAreas';

interface SkillScorePanelProps {
  scores: Record<string, number>;
  overallScore?: number;
  compact?: boolean;
  weeklyDelta?: number;
}

export default function SkillScorePanel({ scores, overallScore, compact = false, weeklyDelta }: SkillScorePanelProps) {
  const overall = overallScore ?? computeOverallScore(scores);

  const scoreColor =
    overall >= 80 ? 'text-mint-700' :
    overall >= 60 ? 'text-accent-700' :
    overall >= 40 ? 'text-sun-600' :
    'text-coral-600';

  const barColor = (score: number) =>
    score >= 80 ? 'bg-mint-500' :
    score >= 60 ? 'bg-accent-500' :
    score >= 40 ? 'bg-sun-500' :
    'bg-coral-500';

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-1">
            AI Skill Score
          </div>
          <div className={`font-display text-5xl font-semibold leading-none ${scoreColor}`}>
            {overall}
          </div>
        </div>
        {weeklyDelta !== undefined && weeklyDelta > 0 && (
          <div className="text-sm text-mint-600 font-medium pb-1">
            +{weeklyDelta} this week
          </div>
        )}
      </div>

      <div className="space-y-2">
        {SKILL_AREAS.map((skill, i) => {
          const score = scores[skill.key] ?? 0;
          return (
            <div key={skill.key} className="flex items-center gap-3">
              <div className="text-xs text-ink-600 w-24 shrink-0">{skill.label}</div>
              <div className="flex-1 h-1.5 bg-ink-100 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full rounded-full ${barColor(score)}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${score}%` }}
                  transition={{ duration: 0.5, delay: i * 0.03, ease: 'easeOut' }}
                />
              </div>
              <div className="text-xs text-ink-500 font-mono w-8 text-right tabular-nums">{score}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
